/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { FC, useMemo } from 'react';
import { groupBy } from 'lodash-es';
import { MdRefresh } from 'react-icons/md';
import { useTranslation } from 'react-i18next';

import Countup from 'components/styled/Countup';
import Text from 'components/styled/Text';
import { Flex } from 'components/styled';
import IconButton from 'components/styled/IconButton';

import { pluralRules } from 'utils';

import { useHomepageStatistics } from 'api/publicApi';

const InfoElem: FC<{ id: string; title: number; subtitle: string }> = ({
	id,
	title,
	subtitle,
}) => (
	<Flex
		flexDirection={['row', 'column']}
		alignItems="center"
		mb={[2, 3, 4]}
		mx={[0, 2, 3, 4]}
	>
		<Countup
			id={id}
			fontSize={[28, 34, 34, 54]}
			fontWeight={500}
			pr={[3, 0]}
			width={['40%', 'unset']}
			textAlign={['right', 'center']}
		>
			{title}
		</Countup>
		<Text
			fontSize="md"
			mt={0}
			pl={[3, 0]}
			width={['40%', 'unset']}
			textAlign={['left', 'center']}
			css={css`
				white-space: nowrap;
			`}
		>
			{subtitle}
		</Text>
	</Flex>
);

const CountupInfo: FC<{ hideInteractions?: boolean }> = ({
	hideInteractions,
}) => {
	const response = useHomepageStatistics();

	const {
		studentTotal: [studentTotal] = [],
		companyTotalVisible: [companyTotalVisible] = [],
		offerTotal: [offerTotal] = [],
		offerSubmittedFormTotal: [offerSubmittedFormTotal] = [],
	} = useMemo(() => groupBy(response.data ?? [], 'statType'), [response.data]);

	const { t } = useTranslation('modules/public/homepage/countupinfo/');

	return (
		<Flex
			flexDirection={['column', 'row']}
			justifyContent="center"
			my={3}
			minHeight={83}
		>
			{response.isError ? (
				<IconButton
					onClick={() => response.refetch()}
					aria-label={t('refresh_stats')}
				>
					<MdRefresh size={22} />
					<Text>{t('refresh_stats')}</Text>
				</IconButton>
			) : (
				<>
					<InfoElem
						id="students_countup"
						title={studentTotal?.count ?? 0}
						subtitle={pluralRules(
							studentTotal?.count ?? 0,
							{
								one: t('student_one'),
								few: t('student_few'),
								many: t('student_many'),
							},
							false,
						)}
					/>
					<InfoElem
						id="companies_countup"
						title={companyTotalVisible?.count ?? 0}
						subtitle={pluralRules(
							companyTotalVisible?.count ?? 0,
							{
								one: t('company_one'),
								few: t('company_few'),
								many: t('company_many'),
							},
							false,
						)}
					/>
					<InfoElem
						id="offers_countup"
						title={offerTotal?.count ?? 0}
						subtitle={pluralRules(
							offerTotal?.count ?? 0,
							{
								one: t('offer_one'),
								few: t('offer_few'),
								many: t('offer_many'),
							},
							false,
						)}
					/>
					{!hideInteractions && (
						<InfoElem
							id="interactions_countup"
							title={offerSubmittedFormTotal?.count ?? 0}
							subtitle={pluralRules(
								offerSubmittedFormTotal?.count ?? 0,
								{
									one: t('interaction_one'),
									few: t('interaction_few'),
									many: t('interaction_many'),
								},
								false,
							)}
						/>
					)}
				</>
			)}
		</Flex>
	);
};

export default CountupInfo;
